// Uploads tab — explanatory blurb + paginated table of SAP imports.
// SPEC §5 "Uploads tab".

import React, { useEffect, useState, useCallback } from 'react';
import { TOKENS, UI_FONT } from './tokens';
import { Card, Mono, Avatar, StatusPill, Icon, styles } from './primitives';
import { fetchUploads } from './api';

const PAGE_SIZE = 20;

export default function UploadsView({ period, refreshKey }) {
  const [uploads, setUploads] = useState([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchUploads(period, { limit: PAGE_SIZE, offset });
      const data = res.data;
      // Backend may return `{imports: [], total}` or just an array — handle
      // both.
      if (Array.isArray(data)) {
        setUploads(data);
        setTotal(data.length);
      } else {
        setUploads(data.imports || data.uploads || []);
        setTotal(data.total ?? (data.imports?.length || 0));
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setUploads([]);
        setTotal(0);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to load uploads');
      }
    } finally {
      setLoading(false);
    }
  }, [period, offset]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE);

  return (
    <div style={{ flex: '1 1 auto', overflow: 'auto', padding: '14px 18px 40px' }}>
      <div style={{ marginBottom: 12 }}>
        <Mono size={10.5} color={TOKENS.ink3}>
          UPLOAD LOG · {period}
        </Mono>
        <div
          style={{
            fontFamily: UI_FONT,
            fontSize: 13,
            color: TOKENS.ink,
            marginTop: 6,
            lineHeight: 1.5,
            maxWidth: 720,
          }}
        >
          SAP pulls are <b>idempotent</b>: each file is keyed by SHA-256 of its
          contents and each row by SAP document number, so re-uploading the
          same export does nothing. New rows are added; everything already in
          the workspace stays untouched.
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 7,
            background: 'oklch(0.96 0.04 25)',
            border: `1px solid ${TOKENS.danger}`,
            color: TOKENS.danger,
            fontSize: 11.5,
            fontFamily: UI_FONT,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <Card padding={0}>
        <table style={{ ...styles.tableBase, marginTop: 0 }}>
          <thead>
            <tr>
              <th style={styles.thStyle}>Upload</th>
              <th style={styles.thStyle}>Period</th>
              <th style={{ ...styles.thStyle, textAlign: 'right' }}>Rows in file</th>
              <th style={{ ...styles.thStyle, textAlign: 'right' }}>Added</th>
              <th style={{ ...styles.thStyle, textAlign: 'right' }}>Unchanged</th>
              <th style={{ ...styles.thStyle, textAlign: 'right' }}>Auto-matched</th>
              <th style={styles.thStyle}>Uploaded</th>
              <th style={styles.thStyle}>By</th>
              <th style={styles.thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {uploads.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    ...styles.tdStyle,
                    textAlign: 'center',
                    color: TOKENS.ink3,
                    padding: '30px 12px',
                  }}
                >
                  No uploads for {period} yet. Click <b>Upload SAP pull</b> to add one.
                </td>
              </tr>
            ) : (
              uploads.map((u) => (
                <tr key={u.id} className="recon-row" style={{ cursor: 'default' }}>
                  <td style={styles.tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Icon
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6"
                        size={13}
                        stroke={TOKENS.ink2}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 500 }}>{u.filename}</span>
                        <Mono size={9.5} color={TOKENS.ink3}>
                          {u.id}
                        </Mono>
                      </div>
                    </div>
                  </td>
                  <td style={styles.tdStyle}>
                    <Mono>{u.quarter || u.period || period}</Mono>
                  </td>
                  <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                    <Mono color={TOKENS.ink2}>
                      {(u.record_count ?? u.rows ?? 0).toLocaleString()}
                    </Mono>
                  </td>
                  <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                    <Mono color={(u.rows_added ?? u.added ?? 0) > 0 ? TOKENS.ok : TOKENS.ink3}>
                      {(u.rows_added ?? u.added ?? 0) > 0 ? '+' : ''}
                      {u.rows_added ?? u.added ?? 0}
                    </Mono>
                  </td>
                  <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                    <Mono color={TOKENS.ink3}>
                      {(u.rows_unchanged ?? u.unchanged ?? 0).toLocaleString()}
                    </Mono>
                  </td>
                  <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                    <Mono color={(u.auto_matched ?? 0) > 0 ? TOKENS.ok : TOKENS.ink3}>
                      {u.auto_matched ?? '—'}
                    </Mono>
                  </td>
                  <td style={styles.tdStyle}>
                    <Mono size={10.5} color={TOKENS.ink3}>
                      {formatDate(u.uploaded_at || u.created_at)}
                    </Mono>
                  </td>
                  <td style={styles.tdStyle}>
                    {u.uploaded_by ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Avatar
                          a={{
                            initials: initialsOf(u.uploaded_by.name),
                            hue: 35,
                          }}
                          size={14}
                        />
                        <span style={{ fontSize: 11.5 }}>{u.uploaded_by.name}</span>
                      </span>
                    ) : (
                      <Mono color={TOKENS.ink3}>—</Mono>
                    )}
                  </td>
                  <td style={styles.tdStyle}>
                    <StatusPill status={u.status || 'processed'} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {pages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={currentPage === 0}
            style={{
              ...styles.ghostBtn,
              opacity: currentPage === 0 ? 0.5 : 1,
              cursor: currentPage === 0 ? 'default' : 'pointer',
            }}
          >
            ← Prev
          </button>
          <Mono size={11} color={TOKENS.ink3}>
            Page {currentPage + 1} / {pages}
          </Mono>
          <button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={currentPage >= pages - 1}
            style={{
              ...styles.ghostBtn,
              opacity: currentPage >= pages - 1 ? 0.5 : 1,
              cursor: currentPage >= pages - 1 ? 'default' : 'pointer',
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function initialsOf(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return s;
  }
}
